import classNames from "classnames";
import { FunctionComponent, useCallback } from "react";
import { useIntl } from "react-intl";
import TooltipSmily from "@Components/atoms/TooltipSmily";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import { ReactComponent as IconHelp } from "@Icons/16x16/help-circle.svg";
import "./style.scss";

interface Props {
  disabled: boolean;
  displayHelpInformation?: boolean;
  errorMessage: string;
  id: string;
  label: string;
  placeholder: string;
}

const CheckoutCreditCardInput: FunctionComponent<Props> = ({
  disabled,
  displayHelpInformation = false,
  errorMessage,
  id,
  label,
  placeholder,
}) => {
  const { isLargeScreen } = useBreakpoint();
  const intl = useIntl();

  const bottomPosition = isLargeScreen ? "40px" : "36px";
  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  }, []);

  return (
    <div
      className={classNames("CheckoutCreditCardInput", {
        "CheckoutCreditCardInput--disabled": disabled,
        "CheckoutCreditCardInput--error": !!errorMessage,
      })}
    >
      {id === "cardholdername" ? (
        <input
          autoComplete="off"
          className="CheckoutCreditCardInput-input"
          id={id}
          onKeyDown={handleKeyDown}
          onPaste={(e) => e.preventDefault()}
          placeholder="&nbsp;"
          required={true}
          type="text"
        />
      ) : (
        <div
          className="CheckoutCreditCardInput-input"
          data-processout-input={id}
          data-processout-placeholder={placeholder}
          id={id}
          tabIndex={1}
        />
      )}
      <label className="CheckoutCreditCardInput-label" htmlFor={id}>
        <div className="CheckoutCreditCardInput-label-line" />
        <div className="CheckoutCreditCardInput-label-text">{label}</div>
      </label>
      {displayHelpInformation && (
        <>
          <IconHelp />
          <TooltipSmily
            arrowPosition="downRight"
            backgroundColor="tertiaryBlue48"
            position={{ bottom: bottomPosition, right: "-2px" }}
          >
            {intl.formatMessage({
              id: "cheerz_CheckoutCreditCardInput_tooltip",
            })}
          </TooltipSmily>
        </>
      )}
      {!!errorMessage && <div className="CheckoutCreditCardInput-errorMessage">{errorMessage}</div>}
    </div>
  );
};

export default CheckoutCreditCardInput;
